import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Send, Plus, Trash2, Loader2, UserCircle2, RotateCcw, Volume2, VolumeX, Lock, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { adminHeaders, sendChat, listSessions, getSession, deleteSession, fetchUserProfile, saveUserProfile, deleteUserProfile, getChatFingerprint, API } from "../lib/api";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";

const PAPICOO_AVATAR = `${process.env.PUBLIC_URL || ""}/papicoo-avatar.png`;

function VoiceButton({ text }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const play = async () => {
    if (state === "playing") {
      audioRef.current?.pause();
      setState("idle");
      return;
    }
    setState("loading");
    try {
      const res = await axios.post(`${API}/voice/synthesize`, { text }, { responseType: "blob", headers: adminHeaders() });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setState("idle"); URL.revokeObjectURL(url); };
      a.onerror = () => { setState("idle"); URL.revokeObjectURL(url); };
      await a.play();
      setState("playing");
    } catch (e) {
      setState("idle");
      if (e?.response?.status === 401) toast.info(T("La lecture vocale est réservée pour le moment. Le texte reste disponible."));
      else if (e?.response?.status === 503) toast.info(T("Le mode vocal n'est pas encore activé sur ce Papicoo."));
      else if (e?.response?.status === 403) toast.error(e?.response?.data?.detail || "Permission ElevenLabs insuffisante.");
      else toast.error(T("Lecture impossible."));
    }
  };
  return (
    <button
      data-testid="voice-play-btn"
      onClick={play}
      title={state === "playing" ? T("Arrêter") : T("Écouter")}
      className="opacity-60 hover:opacity-100 transition shrink-0 p-1 rounded-full hover:bg-[color:var(--papicoo-sand-deep)]/40"
    >
      {(() => {
        if (state === "loading") return <Loader2 size={14} className="animate-spin" />;
        if (state === "playing") return <VolumeX size={14} />;
        return <Volume2 size={14} />;
      })()}
    </button>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"} fade-in-up`}>
      {!isUser && (
        <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-9 h-9 rounded-full object-cover border border-[color:var(--papicoo-gold)] shrink-0" />
      )}
      <div
        data-testid={isUser ? "chat-bubble-user" : "chat-bubble-papicoo"}
        className={`max-w-[85%] sm:max-w-[78%] px-5 py-3 rounded-md whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed text-[15px] ${
          isUser ? "papicoo-bubble-user rounded-br-sm" : "papicoo-bubble-papi rounded-bl-sm"
        }`}
      >
        {content}
        {!isUser && (
          <div className="mt-2 flex justify-end">
            <VoiceButton text={content} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Chat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fingerprint = getChatFingerprint();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftGender, setDraftGender] = useState("");
  const [quota, setQuota] = useState(null);
  const [packages, setPackages] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const scroller = useRef(null);

  const loadQuota = async () => {
    try {
      const { data } = await axios.post(`${API}/chat/quota`, { fingerprint });
      setQuota(data);
    } catch (e) { debugLog("chat quota load failed:", e?.message); }
  };
  const refreshSessions = async () => {
    try { setSessions(await listSessions()); } catch (e) { debugLog("listSessions failed:", e?.message); }
  };
  const refreshProfile = async () => {
    try { setProfile(await fetchUserProfile()); } catch (e) { console.warn("fetchUserProfile failed:", e); }
  };

  useEffect(() => {
    refreshSessions();
    refreshProfile();
    loadQuota();
    axios.get(`${API}/chat/packages`)
      .then((r) => setPackages(r.data || []))
      .catch((err) => console.warn("packages load failed:", err));
  }, []);

  // Stripe return — crédite quota chat
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const paid = params.get("paid");
    if (paid === "1" && sid) {
      axios.get(`${API}/chat/checkout/status/${sid}`).then(({ data }) => {
        if (data.paid) {
          toast.success(`✅ Paiement confirmé — ${data.credited_messages || ""} questions ajoutées !`);
          loadQuota();
          navigate("/chat", { replace: true });
        }
      }).catch(() => {});
    } else if (params.get("cancelled")) {
      toast.info(T("Paiement annulé."));
      navigate("/chat", { replace: true });
    }
  }, []);

  const buyChatPack = async (pkgId) => {
    setCheckoutLoading(pkgId);
    try {
      const { data } = await axios.post(`${API}/chat/checkout`, {
        package_id: pkgId,
        fingerprint,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Impossible de lancer le paiement."));
    } finally {
      setCheckoutLoading(null);
    }
  };
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const openSession = async (id) => {
    try {
      const data = await getSession(id);
      setSessionId(id);
      setMessages(data.messages || []);
    } catch (e) { toast.error(T("Impossible d'ouvrir cette conversation.")); }
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  const remove = async (id) => {
    await deleteSession(id);
    if (sessionId === id) newChat();
    refreshSessions();
    toast.success(T("Conversation supprimée."));
  };

  const submit = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    if (quota && !quota.can_send) {
      setShowPaywall(true);
      return;
    }
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content }]);
    setLoading(true);
    try {
      const data = await sendChat({ session_id: sessionId, message: content });
      setSessionId(data.session_id);
      setMessages((m) => {
        const cleaned = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...cleaned, data.user_message, data.papicoo_message];
      });
      refreshSessions();
      refreshProfile();
      loadQuota();
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowPaywall(true);
        setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
      } else {
        toast.error(T("Papicoo est temporairement silencieux. Réessaie dans un instant."));
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-chat" className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
      <div className="grid lg:grid-cols-[260px_1fr] gap-4 lg:gap-6">
        {/* Sidebar — collapse on mobile by hiding empty state cleanly */}
        <aside className="papicoo-card p-4 h-fit order-2 lg:order-1 lg:sticky lg:top-24">
          <button data-testid="new-chat-btn" onClick={newChat} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full papicoo-btn-primary text-sm">
            <Plus size={16} /> {T("Nouvelle conversation")}
          </button>
          <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Historique")}</div>
          <div className="mt-2 space-y-1 max-h-[55vh] overflow-y-auto">
            {sessions.length === 0 && (
              <div className="text-xs text-[color:var(--papicoo-muted)] py-3 italic">{T("Aucune conversation pour l'instant.")}</div>
            )}
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${sessionId === s.id ? "bg-[color:var(--papicoo-sand-deep)]" : "hover:bg-[color:var(--papicoo-sand-deep)]/60"}`}>
                <button onClick={() => openSession(s.id)} data-testid={`session-${s.id}`} className="flex-1 text-left truncate text-[color:var(--papicoo-ink)]">{T(s.title)}</button>
                <button onClick={() => remove(s.id)} data-testid={`session-del-${s.id}`} className="opacity-0 group-hover:opacity-100 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-accent)]">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Chat surface */}
        <section className="papicoo-card overflow-hidden flex flex-col h-[72vh] lg:h-[78vh] order-1 lg:order-2">
          <div className="px-5 py-4 border-b border-[color:var(--papicoo-border)] flex items-center gap-3 bg-[color:var(--papicoo-surface)]">
            <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-10 h-10 rounded-full object-cover border border-[color:var(--papicoo-gold)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div data-testid="chat-title" className="font-display text-lg font-semibold tracking-[0.12em] text-[color:var(--papicoo-ink)]">{T("PAPICOO")} <span className="text-[10px] align-middle font-sans font-medium tracking-[0.2em] text-[color:var(--papicoo-gold)] border border-[color:var(--papicoo-gold)]/50 px-1.5 py-0.5 ml-1">{T("ASSISTANT IA")}</span></div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] truncate">
                {(() => {
                  if (!quota) return T("En ligne — force tranquille");
                  if (quota.paid_remaining > 0) return <>{T("En ligne ·")} <strong className="text-[color:var(--papicoo-gold)]">{quota.paid_remaining}</strong> {T("messages payés")}</>;
                  if (quota.free_remaining > 0) return <>{T("En ligne ·")} <strong className="text-[color:var(--papicoo-ink)]">{quota.free_remaining}</strong> {T(quota.free_remaining > 1 ? "questions gratuites" : "question gratuite")}</>;
                  return <>{T("Quota épuisé — débloque ci-dessous")}</>;
                })()}
              </div>
            </div>
            <button
              data-testid="profile-edit-btn"
              onClick={() => {
                setDraftName(profile?.name || "");
                setDraftGender(profile?.gender || "");
                setShowProfileEdit((s) => !s);
              }}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/60 whitespace-nowrap max-w-[40vw] sm:max-w-none"
              title={T("Profil")}
            >
              <UserCircle2 size={14} className="text-[color:var(--papicoo-gold)] shrink-0" />
              {profile?.name ? (
                <span className="truncate">{T(profile.name)}{profile.gender ? ` · ${profile.gender}` : ""}</span>
              ) : (
                <span className="text-[color:var(--papicoo-muted)] italic">{T("Identifie-toi")}</span>
              )}
            </button>
          </div>

          {showProfileEdit && (
            <div data-testid="profile-edit-panel" className="px-5 py-3 border-b border-[color:var(--papicoo-border)] bg-[color:var(--papicoo-sand-deep)]/40 grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
              <input
                data-testid="profile-name-input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder={T("Ton prénom")}
                className="bg-white border border-[color:var(--papicoo-border)] rounded-full px-3 py-1.5 text-sm outline-none"
              />
              <select
                data-testid="profile-gender-select"
                value={draftGender}
                onChange={(e) => setDraftGender(e.target.value)}
                className="bg-white border border-[color:var(--papicoo-border)] rounded-full px-3 py-1.5 text-sm outline-none"
              >
                <option value="">{T("— adressage —")}</option>
                <option value="muslim">{T("muslim")}</option>
                <option value="muslima">{T("muslima")}</option>
              </select>
              <button
                data-testid="profile-save-btn"
                onClick={async () => {
                  try {
                    const payload = {};
                    if (draftName.trim()) payload.name = draftName.trim();
                    if (draftGender) payload.gender = draftGender;
                    if (!Object.keys(payload).length) { toast.error(T("Renseigne au moins ton prénom.")); return; }
                    const saved = await saveUserProfile(payload);
                    setProfile(saved);
                    setShowProfileEdit(false);
                    toast.success(T("Saalam — Papicoo te reconnaît maintenant."));
                  } catch { toast.error(T("Échec de l'enregistrement.")); }
                }}
                className="px-3 py-1.5 rounded-full text-xs papicoo-btn-primary"
              >
                {T("Enregistrer")}
              </button>
              <button
                data-testid="profile-reset-btn"
                onClick={async () => {
                  try {
                    await deleteUserProfile();
                    setProfile(null);
                    setDraftName("");
                    setDraftGender("");
                    setShowProfileEdit(false);
                    toast.success(T("Profil effacé."));
                  } catch (e) { console.warn("Profile delete failed:", e); }
                }}
                className="px-3 py-1.5 rounded-full text-xs bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1"
                title={T("Oublier")}
              >
                <RotateCcw size={12} /> {T("Oublier")}
              </button>
            </div>
          )}

          <div ref={scroller} className="flex-1 overflow-y-auto papicoo-chat-bg p-5 space-y-4">
            {messages.length === 0 && (
              <div className="max-w-xl mx-auto text-center py-10">
                <div className="font-display text-3xl font-semibold text-[color:var(--papicoo-ink)]">{T("As-salâmu ‘alaykum.")}</div>
                <p className="text-sm text-[color:var(--papicoo-muted)] mt-2 italic">{T("Demande-moi tout — la foi, l'argent licite, l'amour du verbe, la stratégie. Je m'appuie toujours sur le Coran, la science, la philosophie et notre culture.")}</p>
                <p data-testid="chat-ai-notice" className="text-xs text-[color:var(--papicoo-muted)] mt-3">{T("Assistant IA inspiré de Papicoo — ce n'est pas Papicoo en personne qui répond. Vérifie toujours les références citées.")}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-2">
                  {[t("chat.starter1"), t("chat.starter2"), t("chat.starter3"), t("chat.starter4")].map((s, i) => (
                    <button key={s} data-testid={`starter-${i}`} onClick={() => submit(s)} className="papicoo-card text-left p-3 text-sm hover:-translate-y-0.5 transition-transform">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => <Bubble key={m.id} role={m.role} content={m.content} />)}
            {loading && (
              <div className="flex items-center gap-2 text-[color:var(--papicoo-muted)] text-sm italic">
                <Loader2 size={16} className="animate-spin" /> {T("Papicoo réfléchit…")}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="border-t border-[color:var(--papicoo-border)] p-2 sm:p-3 flex gap-2 bg-[color:var(--papicoo-surface)]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              aria-label={T("Ton message")}
              className="flex-1 min-w-0 bg-[#0b0b0c] text-[color:var(--papicoo-ink)] border border-white/20 rounded-sm px-4 sm:px-5 py-3 outline-none focus:border-[color:var(--papicoo-gold)] text-[15px]"
            />
            <button data-testid="chat-send-btn" type="submit" disabled={loading || !input.trim()} aria-label={T("Envoyer")}
              className="shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-3 papicoo-btn-primary disabled:opacity-50">
              <Send size={16} />
            </button>
          </form>
        </section>
      </div>

      {/* Paywall modal */}
      {showPaywall && (
        <div data-testid="chat-paywall" className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPaywall(false)}>
          <div className="papicoo-card p-7 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Continuer avec Papicoo")}</div>
            <div className="font-display text-2xl text-[color:var(--papicoo-ink)] mt-1">{T("3 questions offertes — tu en veux plus ?")}</div>
            <p className="text-sm text-[color:var(--papicoo-muted)] mt-2">
              {T("Choisis un pack et reprends la conversation. Sans abonnement caché.")}
            </p>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {packages.map((p) => {
                const isAnnual = p.id === "annuel";
                return (
                  <div key={p.id} data-testid={`chat-pack-${p.id}`} className={`papicoo-card p-5 relative ${isAnnual ? "ring-2 ring-[color:var(--papicoo-gold)]" : ""}`}>
                    {isAnnual && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)]">{T("Le plus rentable")}</div>}
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{p.label.split("—")[0].trim()}</div>
                    <div className="mt-1 font-display text-3xl text-[color:var(--papicoo-ink)]">
                      {p.price_eur.toFixed(2).replace(".", ",")}<span className="text-sm align-top"> €</span>
                    </div>
                    <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{p.label.split("—")[1]?.trim()}</div>
                    <ul className="mt-3 space-y-1 text-[11px] text-[color:var(--papicoo-ink)]">
                      <li className="inline-flex items-center gap-1.5"><Check size={11} className="text-[color:var(--papicoo-gold)]" /> {T("Réponses Coran-seul")}</li>
                      <li className="inline-flex items-center gap-1.5"><Check size={11} className="text-[color:var(--papicoo-gold)]" /> {T("Lectures Cheikh Anta Diop, Babou")}</li>
                      <li className="inline-flex items-center gap-1.5"><Check size={11} className="text-[color:var(--papicoo-gold)]" /> {T("Conversations sauvegardées")}</li>
                    </ul>
                    <button
                      data-testid={`chat-buy-${p.id}`}
                      onClick={() => buyChatPack(p.id)}
                      disabled={checkoutLoading === p.id}
                      className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full ${isAnnual ? "papicoo-btn-gold" : "papicoo-btn-primary"} text-xs disabled:opacity-60`}
                    >
                      {checkoutLoading === p.id ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                      Choisir
                    </button>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setShowPaywall(false)} className="mt-4 w-full text-xs text-[color:var(--papicoo-muted)]">{T("Plus tard")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
